import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useStrategy } from '../StrategyContext';
import Tip from '../components/Tip';

interface BotEvent {
  id: string;
  event_type: string;
  message: string;
  metadata: any;
  created_at: string;
}

const BotControl: React.FC = () => {
  const { strategy, strategyLabel } = useStrategy();
  const [status, setStatus] = useState<any>(null);
  const [events, setEvents] = useState<BotEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [st, evt] = await Promise.all([
        api.getBotStatus(strategy).catch(() => null),
        api.getEvents(20, strategy).catch(() => []),
      ]);
      setStatus(st);
      setEvents(evt);
    } catch (err) {
      console.error('BotControl fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [strategy]);

  const isRunning = status?.status === 'running';
  const mode = status?.mode || 'paper';
  const longPos = status?.long_positions ?? 0;
  const flPos = status?.futures_long_positions ?? 0;
  const shortPos = status?.short_positions ?? 0;
  const shortEnabled = status?.short_enabled ?? true;
  const flEnabled = status?.futures_long_enabled ?? false;
  const bullFilterEnabled = status?.bull_filter_enabled ?? true;
  const bearFilterEnabled = status?.bear_filter_enabled ?? true;
  const bullStatus = !bullFilterEnabled ? 'OFF' : status?.bull_filter_status;
  const bearStatus = !bearFilterEnabled ? 'OFF' : status?.bear_filter_status;

  // Compute active mode label
  const activeModes = [true, flEnabled, shortEnabled].filter(Boolean).length;
  const modeLabel = activeModes === 3 ? 'Tri-Mode' :
    (flEnabled && shortEnabled) ? 'Futures Only' :
    (flEnabled && !shortEnabled) ? 'Dual (Spot + Lev. Long)' :
    (!flEnabled && shortEnabled) ? 'Dual (Spot + Short)' : 'Spot Only';

  // Format uptime
  const formatUptime = (uptime: string | null) => {
    if (!uptime) return '—';
    const parts = uptime.split(':');
    if (parts.length >= 2) {
      const hours = parseInt(parts[0]);
      const mins = parseInt(parts[1]);
      if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
      return `${hours}h ${mins}m`;
    }
    return uptime;
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Bot Control</div>
        <div className="header-actions">
          <button className="btn" onClick={fetchData}>&#8635; Refresh</button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="stats-row stats-5">
        <div className="stat-card">
          <div className="stat-label">Status <Tip text="Whether the trading bot process is running on the EC2 server" /></div>
          <div className={`stat-value ${isRunning ? 'green' : 'red'}`}>
            {loading ? '...' : isRunning ? 'Running' : 'Stopped'}
          </div>
          <div className="stat-sub gray">EC2 instance</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Uptime</div>
          <div className="stat-value">{formatUptime(status?.uptime)}</div>
          <div className="stat-sub gray">{status?.last_check ? `Last check: ${new Date(status.last_check).toLocaleTimeString()}` : '—'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Mode <Tip text="Active trading modes. Tri-Mode = Spot Long + Futures Long + Futures Short." /></div>
          <div className={`stat-value ${mode === 'paper' ? 'yellow' : 'green'}`}>
            {modeLabel}
          </div>
          <div className="stat-sub gray">{mode === 'paper' ? 'Paper trading' : 'Live trading'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Positions <Tip text="L = Spot Long, FL = Futures Long, S = Futures Short. Max 4 total across all modes." /></div>
          <div className="stat-value">
            <span style={{ color: '#22c55e' }}>{longPos}L</span>
            {flEnabled && (
              <>
                <span style={{ color: '#a1a1aa', margin: '0 4px' }}>/</span>
                <span style={{ color: '#3b82f6' }}>{flPos}FL</span>
              </>
            )}
            {shortEnabled && (
              <>
                <span style={{ color: '#a1a1aa', margin: '0 4px' }}>/</span>
                <span style={{ color: '#a855f7' }}>{shortPos}S</span>
              </>
            )}
          </div>
          <div className="stat-sub gray">{longPos + flPos + shortPos} total open</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Next Daily Check <Tip text="When the bot scans for new entry/exit signals. Trailing stops checked every 30 min." /></div>
          <div className="stat-value">{status?.next_check || '00:15 UTC'}</div>
          <div className="stat-sub gray">Daily signal scan</div>
        </div>
      </div>

      {/* Market Filter Cards */}
      <div className="stats-row stats-2" style={{ marginBottom: 16 }}>
        <div className="stat-card">
          <div className="stat-label">Bull Filter (Longs) <Tip text="BTC must be above its 200-day moving average for long entries. Protects capital in bear markets." /></div>
          <div className={`stat-value ${bullStatus === 'OFF' ? '' : bullStatus === 'BULL' ? 'green' : bullStatus === 'BEAR' ? 'red' : ''}`} style={bullStatus === 'OFF' ? { color: '#71717a' } : {}}>
            {bullStatus || '—'}
          </div>
          <div className="stat-sub gray">
            {bullStatus === 'OFF' ? 'Bull filter disabled — all long entries allowed' : bullStatus === 'BULL' ? 'BTC above SMA(200) — long entries allowed' : bullStatus === 'BEAR' ? 'BTC below SMA(200) — long entries blocked' : 'Waiting for data'}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Bear Filter (Shorts) <Tip text="Requires a death cross (50-day SMA below 200-day SMA) for short entries. Ensures shorts only in confirmed downtrends." /></div>
          <div className={`stat-value`} style={{ color: bearStatus === 'OFF' ? '#71717a' : bearStatus === 'DEATH_CROSS' && shortEnabled ? '#a855f7' : '#71717a' }}>
            {!shortEnabled ? 'OFF' : bearStatus === 'OFF' ? 'OFF' : bearStatus === 'DEATH_CROSS' ? 'DEATH CROSS' : bearStatus ? 'NO SIGNAL' : '—'}
          </div>
          <div className="stat-sub gray">
            {!shortEnabled
              ? 'Futures short trading disabled'
              : bearStatus === 'OFF'
                ? 'Bear filter disabled — all short entries allowed'
                : bearStatus === 'DEATH_CROSS'
                  ? 'Death cross active — short entries allowed'
                  : 'No death cross — short entries blocked'}
          </div>
        </div>
      </div>

      <div className="grid-equal">
        {/* Controls */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Controls</div>
          </div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#1a1c22', borderRadius: 8 }}>
              <div>
                <div style={{ fontWeight: 600, color: '#e4e4e7' }}>Trading Bot</div>
                <div style={{ fontSize: 12, color: '#a1a1aa' }}>{strategyLabel} — {modeLabel}</div>
              </div>
              <button className="btn" style={{ borderColor: isRunning ? '#ef4444' : '#22c55e', color: isRunning ? '#ef4444' : '#22c55e' }}>
                {isRunning ? 'Stop Bot' : 'Start Bot'}
              </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#1a1c22', borderRadius: 8 }}>
              <div>
                <div style={{ fontWeight: 600, color: '#e4e4e7' }}>Trading Mode</div>
                <div style={{ fontSize: 12, color: '#a1a1aa' }}>Switch between paper and live trading</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className={`btn ${mode === 'paper' ? 'btn-primary' : ''}`} style={{ fontSize: 12, padding: '6px 12px' }}>Paper</button>
                <button className="btn" style={{ fontSize: 12, padding: '6px 12px', opacity: 0.5, cursor: 'not-allowed' }} title="Connect Coinbase API key in Settings first">Live</button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#1a1c22', borderRadius: 8 }}>
              <div>
                <div style={{ fontWeight: 600, color: '#e4e4e7' }}>Force Daily Check</div>
                <div style={{ fontSize: 12, color: '#a1a1aa' }}>Run entry/exit scan now (outside schedule)</div>
              </div>
              <button className="btn">Run Now</button>
            </div>
          </div>
        </div>

        {/* Bot Log */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Bot Log <span className={`badge badge-${mode}`}>{mode === 'paper' ? 'Paper' : 'Live'}</span></div>
            <span style={{ fontSize: 11, color: '#a1a1aa' }}>last {events.length} events</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div className="bot-log">
              {events.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#a1a1aa' }}>
                  No bot events yet. Events will appear when the bot starts reporting to Supabase.
                </div>
              ) : (
                events.map((evt) => {
                  const time = new Date(evt.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  const isError = evt.event_type === 'error';
                  return (
                    <div key={evt.id} className={evt.event_type === 'daily_check' ? '' : 'dim'}>
                      [{time}] {isError && <span style={{ color: '#ef4444' }}>ERROR: </span>}
                      {evt.message}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotControl;
